import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import "./Policies.css";
import NavUser from "../components/NavUser";
import Footer from "../components/Footer";
import SEOHead from "../components/SEO/SEOHead";
import { getOrganizationSchema, getWebSiteSchema } from "../components/SEO/schemaTemplates";
import { createSupportTicket } from "../services/aiChatService";
import {
  FaUserFriends,
  FaCreditCard,
  FaMoneyBillWave,
  FaPercent,
  FaUndo,
  FaSync,
  FaCalendarAlt,
  FaShieldAlt,
  FaInfoCircle,
  FaHandshake,
  FaClock,
  FaCoins,
  FaUniversity,
  FaFileInvoiceDollar,
  FaLock,
  FaAward,
  FaBan,
  FaHeadset,
  FaTimes,
  FaPaperPlane,
  FaCheckCircle,
  FaRobot,
  FaExclamationTriangle,
  FaFileContract,
  FaUserShield,
  FaGavel,
  FaBuilding,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaCheck,
  FaCopy
} from "react-icons/fa";

function Policies({ defaultTab: propDefaultTab }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine initial tab from props, query params, or URL path
  const getInitialTab = useCallback(() => {
    if (propDefaultTab) return propDefaultTab;
    const queryTab = searchParams.get("tab");
    if (queryTab) return queryTab;

    const path = location.pathname.toLowerCase();
    if (path.includes("privacy")) return "privacy";
    if (path.includes("terms") || path.includes("condition")) return "terms";
    if (path.includes("refund") || path.includes("return") || path.includes("cancellation")) return "refunds";
    if (path.includes("payment")) return "payments";
    if (path.includes("withdrawal") || path.includes("payout")) return "withdrawals";
    if (path.includes("dispute") || path.includes("safety")) return "disputes";
    return "terms"; // Default to Terms & Conditions for official compliance
  }, [propDefaultTab, searchParams, location.pathname]);

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [inquiryType, setInquiryType] = useState("cancellation");
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    bookingRef: "",
    priority: "medium",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState("");

  // Sync tab with URL changes
  useEffect(() => {
    const tabFromUrl = getInitialTab();
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [getInitialTab, activeTab]);

  // Auto-fill user details if logged in
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setFormData((prev) => ({
          ...prev,
          name: parsed.full_name || parsed.name || "",
          email: parsed.email || ""
        }));
      }
    } catch {
      // Ignore parse error
    }
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
    window.scrollTo({ top: 220, behavior: "smooth" });
  };

  const handleCopyCurrentLink = (urlPath) => {
    const fullUrl = `${window.location.origin}${urlPath}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2500);
    });
  };

  const handleOpenAiConcierge = () => {
    setShowSupportModal(false);
    window.dispatchEvent(new CustomEvent("open-ai-concierge"));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!formData.name.trim() || !formData.email.trim()) {
      setSubmitError("Please provide your name and email address.");
      return;
    }
    if (!formData.subject.trim()) {
      setSubmitError("Please enter a subject for your inquiry.");
      return;
    }
    if (!formData.message.trim()) {
      setSubmitError("Please describe your policy or dispute question.");
      return;
    }

    try {
      setIsSubmitting(true);
      const fullDescription = `[Category: ${inquiryType.toUpperCase()}]${
        formData.bookingRef ? ` [Booking/Ref: ${formData.bookingRef.trim()}]` : ""
      }\n\n${formData.message.trim()}`;

      const res = await createSupportTicket({
        guestName: formData.name.trim(),
        guestEmail: formData.email.trim(),
        subject: `[Compliance & Policy] ${formData.subject.trim()}`,
        description: fullDescription,
        priority: formData.priority || "medium"
      });

      if (res.success) {
        setSubmitSuccess(res.ticket || { id: "NEW" });
      } else {
        setSubmitError(res.error || "Failed to submit support ticket.");
      }
    } catch (err) {
      setSubmitError(err.error || err.message || "Failed to submit support ticket. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetModalState = () => {
    setShowSupportModal(false);
    setSubmitSuccess(null);
    setSubmitError("");
  };

  const tabs = [
    { id: "terms", label: "Terms & Conditions", icon: <FaFileContract />, badge: "Required" },
    { id: "privacy", label: "Privacy Policy", icon: <FaUserShield />, badge: "Required" },
    { id: "refunds", label: "Return & Refund Policy", icon: <FaUndo />, badge: "Required" },
    { id: "payments", label: "Payment Gateway (Payments.lk)", icon: <FaCreditCard /> },
    { id: "withdrawals", label: "Withdrawals & Payouts", icon: <FaHandshake /> },
    { id: "commissions", label: "Commissions & Splits", icon: <FaPercent /> },
    { id: "disputes", label: "Trust, Safety & Disputes", icon: <FaShieldAlt /> },
    { id: "roles", label: "Ecosystem Roles & KYC", icon: <FaUserFriends /> },
    { id: "coins", label: "Loyalty Coins & Rewards", icon: <FaCoins /> }
  ];

  const getPolicyMeta = () => {
    switch (activeTab) {
      case "terms":
        return {
          title: "Terms & Conditions | SOLOWAY.LK (PVT) LTD",
          description: "Official Terms of Service for Soloway.lk travel platform operated by SOLOWAY.LK (PVT) LTD. Platform rules, booking responsibilities, and merchant disclosures.",
          canonicalPath: "/terms-and-conditions",
        };
      case "privacy":
        return {
          title: "Privacy Policy & PDPA Protection | Soloway.lk",
          description: "Learn how SOLOWAY.LK (PVT) LTD collects, processes, and protects personal data under Sri Lanka Personal Data Protection Act No. 9 of 2022.",
          canonicalPath: "/privacy-policy",
        };
      case "refunds":
        return {
          title: "Cancellation & Refund Policy | Soloway.lk",
          description: "Clear cancellation timelines, 48-hour full refund criteria, host cancellation guarantees, and transparent bank reversal clearing timelines on Soloway.lk.",
          canonicalPath: "/refund-policy",
        };
      case "payments":
        return {
          title: "Payment Methods & Security Disclosures | Soloway.lk",
          description: "Secure online payment disclosures for Payments.lk hosted gateway, 3D-Secure authentication, and zero card data storage on Soloway.lk.",
          canonicalPath: "/policies?tab=payments",
        };
      case "withdrawals":
        return {
          title: "Partner Payouts & Commission Terms | Soloway.lk",
          description: "Automated commission rules, payout eligibility, T+3 business day settlement windows, and direct bank transfer options for hotel owners and tour guides.",
          canonicalPath: "/policies?tab=withdrawals",
        };
      case "disputes":
        return {
          title: "Safety, Disputes & Guest Resolution | Soloway.lk",
          description: "24/7 guest assistance, certified guide standards, emergency SOS contact paths, and multi-tier dispute resolution protocols on Soloway.lk.",
          canonicalPath: "/policies?tab=disputes",
        };
      default:
        return {
          title: "Platform Policies & Governance | Soloway.lk",
          description: "Official legal policies, privacy protections, refund rules, and merchant payment terms for SOLOWAY.LK (PVT) LTD.",
          canonicalPath: "/policies",
        };
    }
  };

  const policyMeta = getPolicyMeta();
  const policySchema = {
    "@context": "https://schema.org",
    "@graph": [
      getWebSiteSchema(),
      getOrganizationSchema(),
      {
        "@type": "WebPage",
        "@id": `https://soloway.lk${policyMeta.canonicalPath}#webpage`,
        "url": `https://soloway.lk${policyMeta.canonicalPath}`,
        "name": policyMeta.title,
        "description": policyMeta.description,
        "isPartOf": { "@id": "https://soloway.lk/#website" }
      }
    ]
  };

  return (
    <div className="policies-page-wrapper">
      <SEOHead
        title={policyMeta.title}
        description={policyMeta.description}
        keywords="soloway terms, soloway privacy policy, soloway refund policy, sri lanka travel policies, payments lk terms, soloway booking cancellation"
        canonicalPath={policyMeta.canonicalPath}
        structuredData={policySchema}
      />
      <NavUser />

      {/* Hero Header */}
      <div className="policies-hero">
        <div className="policies-hero-content">
          <div className="policies-badge">
            <FaShieldAlt /> Soloway Trust, Governance & Legal Framework
          </div>
          <h1>Platform Policies & Legal Terms</h1>
          <p>
            Official terms of service, privacy data protections, cancellation & refund criteria, and merchant payment terms for SOLOWAY.LK (PVT) LTD.
          </p>

          <div className="policies-quick-stats">
            <div className="quick-stat-item">
              <span className="stat-val">100% Secure</span>
              <span className="stat-lbl">Payments.lk Gateway</span>
            </div>
            <div className="quick-stat-divider" />
            <div className="quick-stat-item">
              <span className="stat-val">48h Full Refund</span>
              <span className="stat-lbl">Flexible Cancellation</span>
            </div>
            <div className="quick-stat-divider" />
            <div className="quick-stat-item">
              <span className="stat-val">Zero Card Storage</span>
              <span className="stat-lbl">Hosted 3D-Secure</span>
            </div>
            <div className="quick-stat-divider" />
            <div className="quick-stat-item">
              <span className="stat-val">Democratic Republic of Sri Lanka</span>
              <span className="stat-lbl">Governing Jurisdiction</span>
            </div>
          </div>
        </div>
      </div>

      <div className="policies-container">
        <div className="policies-layout">
          {/* Sidebar Navigation Tabs */}
          <aside className="policies-tab-sidebar">
            <div className="sidebar-heading">Legal & Operational Policies</div>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`policy-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <span className="tab-btn-icon">{tab.icon}</span>
                <span className="tab-btn-label">{tab.label}</span>
                {tab.badge && <span className="tab-required-badge">{tab.badge}</span>}
              </button>
            ))}

            <div className="sidebar-support-card">
              <FaHeadset className="support-card-icon" />
              <h4>Compliance & Support Desk</h4>
              <p>24/7 assistance for policy clarifications, refund tracking, and official disputes.</p>
              <button
                type="button"
                className="sidebar-contact-btn"
                onClick={() => setShowSupportModal(true)}
              >
                Contact Compliance
              </button>
            </div>

            <div className="sidebar-merchant-info">
              <div className="merchant-info-title"><FaBuilding /> Registered Entity</div>
              <p><strong>SOLOWAY.LK (PVT) LTD</strong></p>
              <p style={{ fontSize: '12px', color: '#475569' }}>Company No. <strong>PV 00373363</strong></p>
              <p>Colombo, Sri Lanka</p>
              <p>Email: <a href="mailto:support@soloway.lk">support@soloway.lk</a></p>
              <p>Card Gateway: <strong>Payments.lk</strong> (Brand operated by Payable (Pvt) Ltd)</p>
              <p>In-Force Policy: <code>v2026.09.01</code> (Effective: Sept 1, 2026)</p>
              <p style={{ fontSize: '11px', color: '#b45309', marginTop: '4px' }}>Draft Revision: <code>v2.0.0</code> (Target: Oct 25, 2026 — Draft Pending Legal Counsel Sign-Off)</p>
            </div>
          </aside>

          {/* Main Policy Content Area */}
          <main className="policies-content-card">

            {/* ========================================================= */}
            {/* TAB: TERMS & CONDITIONS */}
            {/* ========================================================= */}
            {activeTab === "terms" && (
              <div className="policy-section-content animate-fade-in">
                {/* Policy Meta Banner */}
                <div className="policy-meta-bar">
                  <div className="meta-left">
                    <span className="meta-tag">Legal Agreement</span>
                    <span className="meta-date">In-Force: Version 2026.09.01 | Draft: Version 2.0.0 (Target: October 25, 2026 — Draft Pending Approval)</span>
                    <span className="meta-jurisdiction">Jurisdiction: Sri Lanka</span>
                  </div>
                  <button
                    className="copy-policy-link-btn"
                    onClick={() => handleCopyCurrentLink("/terms-and-conditions")}
                    title="Copy direct link to Terms and Conditions"
                  >
                    {copiedUrl ? <FaCheck /> : <FaCopy />} {copiedUrl ? "Link Copied!" : "Copy Terms URL"}
                  </button>
                </div>

                <div className="section-title-wrap">
                  <div className="section-icon-badge blue"><FaFileContract /></div>
                  <div>
                    <h2>Business Terms & Conditions of Service</h2>
                    <p className="section-subtitle">
                      Binding terms governing user accounts, booking transactions, host/guide service standards, payment clearing, and dispute adjudication on Soloway.
                    </p>
                  </div>
                </div>

                <div className="policy-prose-block">
                  <h3>1. Introduction & Acceptance of Terms</h3>
                  <p>
                    Welcome to <strong>Soloway</strong> ("the Platform"), operated by <strong>SOLOWAY.LK (PVT) LTD</strong> (Company Registration No. <strong>PV 00373363</strong>, incorporated under the Companies Act No. 7 of 2007) (hereinafter referred to as "Soloway", "we", "us", or "our").
                  </p>
                  <p>
                    By accessing, registering on, browsing, or utilizing the Soloway website (<a href="https://soloway.lk">https://soloway.lk</a>), mobile applications, APIs, or related travel services, you ("User", "Guest", "Traveler", "Hotel Host", "Tour Guide", or "Partner") acknowledge that you have read, understood, and irrevocably agree to be bound by these Terms & Conditions, together with our Privacy Policy, Return & Refund Policy, and applicable community guidelines. If you do not agree to these terms, you must immediately cease all access and use of the Platform.
                  </p>

                  <h3>2. Platform Nature & Scope of Services</h3>
                  <p>
                    Soloway provides an online digital marketplace and discovery platform designed to connect domestic and international travelers with independent third-party service providers, including certified tourist guides, boutique hotels, eco-villas, homestays, and tour operators across Sri Lanka.
                  </p>
                  <div className="policy-callout-box info">
                    <FaInfoCircle className="callout-icon" />
                    <div>
                      <strong>Marketplace Role:</strong> Soloway acts solely as an intermediary technology platform facilitating reservations and payment processing. Contracts for accommodation, excursions, and guiding services are formed directly between the Guest and the independent Host/Guide. Soloway maintains strict quality onboarding and verified review systems to uphold platform excellence.
                    </div>
                  </div>

                  <h3>3. User Accounts & Identity Verification (KYC)</h3>
                  <ul>
                    <li><strong>Eligibility:</strong> You must be at least 18 years of age and possess the legal authority to enter into binding legal agreements to create an account or execute transactions on Soloway.</li>
                    <li><strong>Account Accuracy:</strong> Users agree to provide true, accurate, current, and complete information during registration and booking checkout. Impersonation of another person or entity is strictly prohibited.</li>
                    <li><strong>Credential Security:</strong> You are solely responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. Soloway must be notified immediately of any unauthorized account access.</li>
                    <li><strong>Provider KYC:</strong> Hotel Owners and Tour Guides must undergo mandatory identity, business registration, property authority, and licensing verification (including Sri Lanka Tourism Development Authority credentials where applicable) prior to receiving booking payouts.</li>
                  </ul>

                  <h3>4. Payment Processing via External Hosted Gateway</h3>
                  <ul>
                    <li><strong>Gateway Identification:</strong> SoloWay utilizes <strong>Payments.lk</strong>, a payments platform brand operated by <strong>Payable (Pvt) Ltd</strong>, as the external payment gateway for new online credit and debit card checkouts.</li>
                    <li><strong>External Hosted Checkout:</strong> When initiating an online card payment for accommodations, tour guide services, visa applications, or event ticketing, customers are redirected to Payments.lk's secure hosted checkout environment to input payment credentials and complete 3D-Secure bank authentication.</li>
                    <li><strong>Zero Card Data Retention:</strong> SoloWay does not capture, receive, process, or store payment card numbers (PAN), expiration dates, or card security codes (CVV/CVC) through its applications or servers. All payment credential handling is performed exclusively within Payments.lk / Payable's PCI-DSS Level 1 compliant infrastructure.</li>
                    <li><strong>Pre-Payment Disclosures:</strong> The exact payment amount, applicable currency (LKR or USD equivalent), itemized charges, and booking reference are clearly presented to the customer prior to checkout redirection.</li>
                    <li><strong>Non-Affiliation Disclaimer:</strong> SoloWay (SOLOWAY.LK (PVT) LTD) is an independent technology platform and travel marketplace. SoloWay does not own, operate, or represent Payments.lk or Payable (Pvt) Ltd.</li>
                  </ul>

                  <h3>5. Payment Confirmation, Asynchronous Verification & Pending Status</h3>
                  <ul>
                    <li><strong>Checkout Initiation vs. Completion:</strong> Initiating checkout or loading the external payment screen does not mean a payment has succeeded or that a reservation is legally confirmed.</li>
                    <li><strong>Browser Return Disclaimer:</strong> Returning or being redirected to SoloWay after visiting the hosted checkout page does not independently constitute proof of successful payment.</li>
                    <li><strong>Server-Side Verification:</strong> A reservation is legally confirmed ONLY when SoloWay receives and cryptographically validates an authoritative server-to-server webhook notification or supported verification callback from the payment gateway.</li>
                    <li><strong>Pending State:</strong> Until authoritative payment confirmation is persisted in SoloWay's database, the booking remains in a <code>PENDING_PAYMENT</code> status. If a checkout session fails, is cancelled by the user, or expires, the booking remains unconfirmed and is released in accordance with platform inventory rules.</li>
                    <li><strong>Deduction Without Immediate Confirmation:</strong> If money is deducted from a customer's bank account or card balance but the booking continues to display a pending or unconfirmed status, the customer should immediately contact SoloWay Support (<a href="mailto:support@soloway.lk">support@soloway.lk</a>) with their Booking Reference and external payment transaction reference (where available).</li>
                    <li><strong>Credential Protection Warning:</strong> SoloWay support representatives will NEVER request, and customers must NEVER provide, full card numbers, CVVs, expiration dates, banking passwords, or OTPs.</li>
                  </ul>

                  <h3>6. Booking Responsibilities & Service Disclaimers</h3>
                  <ul>
                    <li><strong>Marketplace Contract:</strong> SoloWay facilitates bookings and collects payments as an intermediary technology platform. The substantive contract for accommodation stay, guided excursions, or event attendance is formed directly between the traveler and the respective accommodation host, tour guide, or event organizer.</li>
                    <li><strong>Service Availability & Check-in:</strong> Listing accuracy, room availability, check-in arrangements, guided itineraries, physical safety, and service fulfillment remain the legal responsibility of the booked service provider, subject to SoloWay's verified community standards.</li>
                    <li><strong>Payment Gateway Role:</strong> Payments.lk operates solely as payment infrastructure for funds collection. Payments.lk does not guarantee accommodation quality, host fulfillment, tour guide performance, travel safety, refund eligibility, or the outcome of any customer dispute.</li>
                  </ul>

                  <h3>7. Payment Failures, Reconciliation & Dispute Inquiries</h3>
                  <p>
                    Customers may contact the SoloWay Compliance & Support Desk regarding:
                  </p>
                  <ul>
                    <li>Payment deductions where booking confirmation was delayed or not received.</li>
                    <li>Apparent duplicate card charges resulting from interrupted browser sessions.</li>
                    <li>Discrepancies between quoted checkout prices and charged amounts.</li>
                    <li>Refund tracking, cancellation status verification, or listing quality claims under the SafeStay Guarantee.</li>
                  </ul>
                  <p>
                    <strong>Internal Reconciliation Process:</strong> Upon receiving an inquiry, SoloWay compliance staff review the transaction ledger (<code>payment_transactions</code>), match the gateway checkout reference, inspect authoritative provider webhook deliveries, and reconcile with clearing bank settlement records. SoloWay does not promise arbitrary third-party bank clearing times or guarantee dispute decisions outside documented statutory consumer protections and verified contractual terms.
                  </p>

                  <h3>8. Provider Obligations & Service Standards</h3>
                  <ul>
                    <li><strong>Honest Representation:</strong> Hosts and Guides warrant that their listings, photographs, amenities, room availability, and tour itineraries are accurate, current, and not misleading.</li>
                    <li><strong>Service Fulfillment:</strong> Confirmed reservations must be honored in full. In the extraordinary event of host cancellation or overbooking, the host is subject to platform penalties and must assist Soloway in securing equivalent or superior accommodation for the traveler.</li>
                    <li><strong>Compliance with Laws:</strong> All providers must adhere to Sri Lankan labor laws, safety regulations, public health standards, environmental conservation laws, and tourism guidelines.</li>
                  </ul>

                  <h3>9. Guest Conduct & Property Guidelines</h3>
                  <p>
                    Guests agree to respect the house rules, check-in/check-out times, noise regulations, and cultural norms of booked accommodations and tour sites. Guests are financially responsible for any willful or negligent damage caused to a provider's property, fixtures, or equipment during their stay or excursion.
                  </p>

                  <h3>10. Intellectual Property Rights</h3>
                  <p>
                    All content, UI designs, graphics, source code, logos, trademarks, database structures, and software powering Soloway are the intellectual property of SOLOWAY.LK (PVT) LTD and protected under Sri Lankan and international copyright and intellectual property treaties. No portion of the platform may be scraped, reverse-engineered, reproduced, or exploited without express written permission.
                  </p>

                  <h3>11. Limitation of Liability & Disclaimers</h3>
                  <p>
                    To the maximum extent permitted by applicable law, Soloway provides the platform on an "AS IS" and "AS AVAILABLE" basis. Soloway shall not be liable for any indirect, punitive, incidental, special, or consequential damages arising from:
                  </p>
                  <ul>
                    <li>Acts, omissions, defaults, or personal injuries caused by independent third-party hosts, drivers, or tour guides.</li>
                    <li>Natural disasters, adverse weather conditions, political strikes, civil disturbances, or government travel restrictions (Force Majeure events).</li>
                    <li>Temporary technical outages, payment gateway downtime, or maintenance disruptions.</li>
                  </ul>

                  <h3>12. Termination & Account Suspension</h3>
                  <p>
                    Soloway reserves the right to suspend, restrict, or permanently terminate user accounts that engage in fraudulent transactions, abusive conduct toward hosts or travelers, repeated no-shows, violation of local tourism regulations, or breach of these Terms.
                  </p>

                  <h3>13. Governing Law & Dispute Jurisdiction</h3>
                  <div className="policy-callout-box success">
                    <FaGavel className="callout-icon" />
                    <div>
                      <strong>Governing Law:</strong> These Terms & Conditions shall be governed by, construed, and enforced in accordance with the substantive laws of the <strong>Democratic Socialist Republic of Sri Lanka</strong>. Any dispute, claim, or controversy arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the competent courts of <strong>Colombo, Sri Lanka</strong>.
                    </div>
                  </div>

                  <h3>14. Official Contact & Regulatory Inquiries</h3>
                  <div className="official-contact-card">
                    <h4>SOLOWAY.LK (PVT) LTD — Legal & Governance Office</h4>
                    <p><FaBuilding /> Company Registration No. PV 00373363</p>
                    <p><FaMapMarkerAlt /> Colombo, Western Province, Sri Lanka</p>
                    <p><FaEnvelope /> Legal Inquiries: <a href="mailto:legal@soloway.lk">legal@soloway.lk</a> | Support: <a href="mailto:support@soloway.lk">support@soloway.lk</a></p>
                    <p><FaPhoneAlt /> Customer Hotline: +94 11 234 5678 / +94 77 123 4567</p>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* TAB: PRIVACY POLICY */}
            {/* ========================================================= */}
            {activeTab === "privacy" && (
              <div className="policy-section-content animate-fade-in">
                {/* Policy Meta Banner */}
                <div className="policy-meta-bar">
                  <div className="meta-left">
                    <span className="meta-tag">Data Protection</span>
                    <span className="meta-date">In-Force: Version 2026.09.01 | Draft: Version 2.0.0 (Target: October 25, 2026 — Draft Pending Approval)</span>
                    <span className="meta-jurisdiction">PDPA Act No. 9 of 2022 Compliant</span>
                  </div>
                  <button
                    className="copy-policy-link-btn"
                    onClick={() => handleCopyCurrentLink("/privacy-policy")}
                    title="Copy direct link to Privacy Policy"
                  >
                    {copiedUrl ? <FaCheck /> : <FaCopy />} {copiedUrl ? "Link Copied!" : "Copy Privacy URL"}
                  </button>
                </div>

                <div className="section-title-wrap">
                  <div className="section-icon-badge green"><FaUserShield /></div>
                  <div>
                    <h2>Privacy & Personal Data Protection Policy</h2>
                    <p className="section-subtitle">
                      Comprehensive personal data handling, hosted payment processing disclosures, and statutory data subject rights for SoloWay.
                    </p>
                  </div>
                </div>

                <div className="policy-prose-block">
                  <div className="policy-callout-box security">
                    <FaLock className="callout-icon" />
                    <div>
                      <strong>Critical Hosted Checkout & Card Data Security Disclosure:</strong>
                      <p>
                        SOLOWAY.LK (PVT) LTD <strong>DOES NOT</strong> collect, receive, process, transmit, or store sensitive payment card credentials (such as full 16-digit Primary Account Numbers (PAN), card expiration dates, CVV2/CVC security codes, or banking PINs) on its web servers, databases, or application instances. All online credit and debit card payments are handled via external hosted checkout operated by <strong>Payments.lk</strong>, a payment gateway brand of <strong>Payable (Pvt) Ltd</strong>, protected by standard TLS encryption and 3D-Secure bank authentication.
                      </p>
                    </div>
                  </div>

                  <h3>1. Scope & Commitment to Privacy</h3>
                  <p>
                    SOLOWAY.LK (PVT) LTD ("SoloWay", "we", "our") is committed to protecting the privacy, confidentiality, and security of personal information entrusted to us by travelers, property hosts, tour guides, and platform users. This Privacy Policy outlines our data processing practices in compliance with the <strong>Personal Data Protection Act No. 9 of 2022 (PDPA)</strong> of Sri Lanka.
                  </p>

                  <h3>2. Information We Collect & Sources</h3>
                  <div className="privacy-data-grid">
                    <div className="data-type-card">
                      <h4>Account & Identity Information</h4>
                      <ul>
                        <li>Full Name, Salutation, and Contact Information</li>
                        <li>Email Address and Verified Mobile Phone Number</li>
                        <li>Nationality and Country of Residence</li>
                        <li>Account authentication credentials (hashed passwords)</li>
                      </ul>
                    </div>

                    <div className="data-type-card">
                      <h4>Booking & Transaction Records</h4>
                      <ul>
                        <li>Selected accommodations, tour itineraries, or event tickets</li>
                        <li>Reservation dates, guest counts, and special requests</li>
                        <li>Order totals, currency (LKR/USD), and payment timestamps</li>
                        <li>Transaction references and gateway status logs (tokenized checkout IDs)</li>
                      </ul>
                    </div>

                    <div className="data-type-card">
                      <h4>Travel & Regulatory Compliance Documents</h4>
                      <ul>
                        <li>Passport bio-data and National Identity Card (NIC) details (mandatory for SLTDA hotel guest registers and tour safety manifests)</li>
                        <li>Department of Immigration visa application information and uploaded supporting travel permits</li>
                        <li>Host property ownership deeds and Guide licensing credentials</li>
                      </ul>
                    </div>

                    <div className="data-type-card">
                      <h4>Technical & Device Telemetry</h4>
                      <ul>
                        <li>IP address, operating system, and browser user-agent</li>
                        <li>Session tokens, referring URLs, and page access timestamps</li>
                        <li>Security log entries for fraud monitoring and brute-force prevention</li>
                      </ul>
                    </div>
                  </div>

                  <h3>3. Separation of Travel KYC Documents from Payment Processing</h3>
                  <p>
                    Travel verification documents—such as passport copies, NIC numbers, visa documents, and property deeds—are collected strictly to satisfy legal and safety obligations under Sri Lankan tourism regulations and Department of Immigration directives. These travel documents are stored in secure, access-controlled repositories with encrypted channels and are <strong>strictly segregated from payment processing infrastructure</strong>. Payment gateway partners do not receive or access your passport copies or travel identification documents.
                  </p>

                  <h3>4. Purposes for Data Processing</h3>
                  <p>SoloWay processes personal data strictly for legitimate operational and legal purposes, including:</p>
                  <ul>
                    <li><strong>Booking Fulfillment:</strong> Facilitating reservations between guests and independent hosts or guides.</li>
                    <li><strong>Payment Reconciliation & Auditing:</strong> Recording gateway transaction IDs, verifying payment completion via secure webhooks, preventing duplicate billing, and processing authorized refunds.</li>
                    <li><strong>Fraud Prevention & Platform Security:</strong> Detecting suspicious login attempts, validating checkout integrity, and mitigating chargebacks.</li>
                    <li><strong>Customer Support:</strong> Assisting travelers with booking modifications, pending payment resolutions, and dispute mediation.</li>
                    <li><strong>Statutory Accounting:</strong> Retaining financial transaction ledgers to comply with Sri Lankan tax, audit, and commercial accounting laws.</li>
                  </ul>

                  <h3>5. Information Sharing with External Providers</h3>
                  <p>
                    <strong>SoloWay never sells, rents, or commercializes personal data to third-party marketing companies or data brokers.</strong> Personal information is shared only under strict operational conditions:
                  </p>
                  <ul>
                    <li><strong>Booked Service Providers (Hotels & Guides):</strong> Guest contact details and reservation requirements are shared with the specific booked provider solely to fulfill check-in and excursion services.</li>
                    <li><strong>Payment Processing Partners:</strong> Relevant transaction identifiers, booking amounts, currency, customer names, and contact emails are transmitted to external payment gateways (Payments.lk / Payable (Pvt) Ltd, or Koko for eligible installment checkouts) to initiate hosted checkout and verify payment settlement. Payment gateways do not receive travel KYC documents, passports, or visa records.</li>
                    <li><strong>Statutory & Legal Requirements:</strong> Information is disclosed only when required by valid court order, lawful subpoena, or authorized Sri Lankan regulatory directives (e.g. SLTDA, Department of Immigration).</li>
                  </ul>

                  <h3>6. Data Retention, Security & Integrity</h3>
                  <p>
                    SoloWay employs administrative, physical, and technical safeguards including:
                  </p>
                  <ul>
                    <li>Transport Layer Security (TLS 1.2/1.3 with 256-bit cipher suites) for all data in transit.</li>
                    <li>Segregated database environments with role-based administrative access and automated audit trails.</li>
                    <li>Financial transaction ledgers and audit records are retained for statutory accounting and dispute verification (exact retention period subject to formal legal counsel determination, currently proposed as 5 to 7 years in draft policy pending legal sign-off).</li>
                  </ul>

                  <h3>7. Data Subject Rights under the PDPA</h3>
                  <p>Subject to statutory exceptions under Sri Lankan law, you have the right to:</p>
                  <ul>
                    <li><strong>Access:</strong> Request confirmation of whether SoloWay holds your personal data and obtain a copy.</li>
                    <li><strong>Rectification:</strong> Request correction of inaccurate, incomplete, or outdated information.</li>
                    <li><strong>Erasure:</strong> Request deletion of personal data where retention is no longer necessary for contractual fulfillment or legal compliance.</li>
                    <li><strong>Withdrawal of Consent:</strong> Unsubscribe from non-essential promotional communications at any time.</li>
                  </ul>

                  <h3>8. Contact the Privacy & Compliance Team</h3>
                  <p>
                    For inquiries regarding this Privacy Policy, data access requests, or regulatory questions:
                  </p>
                  <div className="official-contact-card">
                    <h4>SoloWay Data Protection & Governance Office</h4>
                    <p><FaEnvelope /> Email: <a href="mailto:privacy@soloway.lk">privacy@soloway.lk</a> | Support: <a href="mailto:support@soloway.lk">support@soloway.lk</a></p>
                    <p><FaBuilding /> Entity: SOLOWAY.LK (PVT) LTD (Company No. PV 00373363)</p>
                    <p><FaMapMarkerAlt /> Address: Colombo, Western Province, Sri Lanka</p>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* TAB: CANCELLATIONS, RETURNS & REFUNDS */}
            {/* ========================================================= */}
            {activeTab === "refunds" && (
              <div className="policy-section-content animate-fade-in">
                {/* Policy Meta Banner */}
                <div className="policy-meta-bar">
                  <div className="meta-left">
                    <span className="meta-tag">Refund Policy</span>
                    <span className="meta-date">In-Force: Version 2026.09.01 | Draft: Version 2.0.0 (Target: October 25, 2026 — Draft Pending Approval)</span>
                    <span className="meta-jurisdiction">Sri Lanka Consumer Law Aligned</span>
                  </div>
                  <button
                    className="copy-policy-link-btn"
                    onClick={() => handleCopyCurrentLink("/return-policy")}
                    title="Copy direct link to Return & Refund Policy"
                  >
                    {copiedUrl ? <FaCheck /> : <FaCopy />} {copiedUrl ? "Link Copied!" : "Copy Return Policy URL"}
                  </button>
                </div>

                <div className="section-title-wrap">
                  <div className="section-icon-badge red"><FaUndo /></div>
                  <div>
                    <h2>Return, Cancellation & Refund Policy</h2>
                    <p className="section-subtitle">
                      Transparent, time-based cancellation tiers, auditable refund calculation rules, multi-stage approval workflows, and gateway settlement schedules.
                    </p>
                  </div>
                </div>

                {/* Tiered Cancellation Timeline Matrix */}
                <h3 className="policy-subsection-title"><FaCalendarAlt /> 1. Standard Booking Cancellation Tiers</h3>
                <div className="cancellation-tier-grid">
                  <div className="tier-card tier-full">
                    <div className="tier-header">
                      <span className="tier-badge green">100% Full Refund</span>
                      <h4>More than 48 Hours Before Arrival</h4>
                    </div>
                    <div className="tier-body">
                      <p>Full cancellation eligibility is granted when requested at least 48 hours prior to scheduled check-in time (2:00 PM local time on check-in date) or tour departure time.</p>
                      <ul className="tier-points">
                        <li><strong>100% Refund:</strong> Traveler receives a 100% refund of the total booking payment.</li>
                        <li><strong>Rebooking Option:</strong> 100% amount can be transferred instantly to SoloWay Wallet Credit with zero processing fees.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="tier-card tier-partial">
                    <div className="tier-header">
                      <span className="tier-badge yellow">50% Partial Refund</span>
                      <h4>24 to 48 Hours Before Arrival</h4>
                    </div>
                    <div className="tier-body">
                      <p>Cancellations requested between 24 and 48 hours prior to scheduled check-in or tour departure qualify for a 50% refund.</p>
                      <ul className="tier-points">
                        <li><strong>50% Traveler Refund:</strong> Credited back to original card or bank account via payment gateway settlement.</li>
                        <li><strong>50% Provider Share:</strong> Disbursed to host or guide to compensate for reserved, locked dates.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="tier-card tier-none">
                    <div className="tier-header">
                      <span className="tier-badge red">Non-Refundable</span>
                      <h4>Less than 24 Hours or No-Show</h4>
                    </div>
                    <div className="tier-body">
                      <p>Cancellations requested under 24 hours from arrival time or failure to show up (No-Show) are strictly non-refundable.</p>
                      <ul className="tier-points">
                        <li><strong>0% Traveler Refund:</strong> Protects local hosts and guides from last-minute vacancy losses.</li>
                        <li><strong>Provider Allocation:</strong> Net provider share released according to standard settlement rules.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Flexible Rebooking Highlight */}
                <div className="special-policy-banner rebook-banner">
                  <div className="banner-icon"><FaSync className="spin-on-hover" /></div>
                  <div className="banner-text">
                    <h3>Flexible Wallet Rebooking Credit</h3>
                    <p>
                      Need to reschedule your Sri Lanka holiday? You can convert eligible refund amounts directly into 
                      <strong> SoloWay Wallet Credit</strong> for immediate rebooking across any hotel, villa, or guided experience on the platform with instant confirmation and zero bank clearing latency.
                    </p>
                  </div>
                </div>

                {/* Detailed Refund Engine & Bank Settlement Rules */}
                <div className="policy-prose-block">
                  <h3>2. Multi-Stage Refund Governance & Lifecycle</h3>
                  <p>
                    SoloWay enforces an immutable multi-stage refund governance workflow to ensure financial integrity:
                  </p>
                  <div className="clearance-timeline">
                    <div className="timeline-step">
                      <div className="step-badge">1. REQUESTED</div>
                      <h4>Cancellation Request</h4>
                      <p>Traveler submits cancellation via portal or compliance desk. Refund engine validates tier eligibility.</p>
                    </div>
                    <div className="timeline-arrow">➔</div>
                    <div className="timeline-step">
                      <div className="step-badge">2. APPROVED</div>
                      <h4>Compliance Review</h4>
                      <p>SoloWay automatically or administratively verifies refund breakdown, commission clawbacks, and provider shares.</p>
                    </div>
                    <div className="timeline-arrow">➔</div>
                    <div className="timeline-step">
                      <div className="step-badge">3. SUBMITTED</div>
                      <h4>Gateway Transmission</h4>
                      <p>Refund instruction transmitted to Payments.lk / payment provider or authorized manual banking settlement.</p>
                    </div>
                    <div className="timeline-arrow">➔</div>
                    <div className="timeline-step">
                      <div className="step-badge">4. SETTLED</div>
                      <h4>Bank Clearing</h4>
                      <p>Funds credited back to traveler's original card or bank account. Authoritative settlement confirmation recorded.</p>
                    </div>
                  </div>

                  <h3>3. Payment Gateway Refund Processing & Settlement Schedules</h3>
                  <div className="policy-info-grid">
                    <div className="policy-detail-card">
                      <div className="detail-card-header">
                        <FaCreditCard className="detail-icon" />
                        <h4>Card & Online Gateway Refunds (Payments.lk / Payable)</h4>
                      </div>
                      <p>For payments completed via credit or debit card through Payments.lk hosted checkout:</p>
                      <ul>
                        <li><strong>Initiation:</strong> SoloWay initiates approved refund requests within <strong>24–48 hours</strong> of verified cancellation.</li>
                        <li><strong>Settlement Timeline:</strong> Following refund approval and gateway submission, funds are credited back to the <strong>original payment card or clearing bank account</strong>. Actual posting and account credit timelines depend entirely on the customer's card-issuing bank clearing schedules and international card network processing rules (typically completing over several business banking days; no fixed completion window or instant refund is guaranteed by SoloWay or Payments.lk).</li>
                        <li><strong>Authoritative Confirmation:</strong> A booking is not marked as fully refunded until authoritative settlement confirmation is received and logged in the immutable audit ledger.</li>
                        <li><strong>Fallback Authorized Manual Process:</strong> If automated gateway refund processing is unavailable or not supported for a particular card scheme or merchant arrangement, SoloWay Finance executes an authorized direct electronic bank transfer (CEFT/SLIPS) without charging arbitrary processing fees.</li>
                      </ul>
                    </div>

                    <div className="policy-detail-card">
                      <div className="detail-card-header">
                        <FaExclamationTriangle className="detail-icon warning" />
                        <h4>Host-Initiated Cancellations</h4>
                      </div>
                      <p>If an accommodation host or tour guide cancels a confirmed booking:</p>
                      <ul>
                        <li>The traveler receives an <strong>immediate 100% full refund</strong>.</li>
                        <li>SoloWay 24/7 Concierge assists in securing an equivalent or superior alternative booking under the <strong>SafeStay Guarantee</strong>.</li>
                        <li>The host incurs a platform cancellation penalty (10% standard fee) and listing quality review.</li>
                      </ul>
                    </div>
                  </div>

                  <h3>4. Non-Refundable Services</h3>
                  <p>The following services are strictly non-refundable once initiated:</p>
                  <ul>
                    <li>Completed hotel stays and concluded guided tours.</li>
                    <li>Government tourist visa (ETA) processing fees once submitted to the Department of Immigration & Emigration.</li>
                    <li>Optional travel insurance policy premiums once the insurance certificate has been underwritten and issued.</li>
                  </ul>

                  <h3>5. How to Request a Cancellation or Track a Refund</h3>
                  <div className="steps-container">
                    <div className="step-card">
                      <span className="step-number">1</span>
                      <h5>Self-Service Online</h5>
                      <p>Sign in to your SoloWay account, navigate to <strong>My Bookings</strong>, select your reservation, and click <strong>Cancel Booking</strong>.</p>
                    </div>
                    <div className="step-card">
                      <span className="step-number">2</span>
                      <h5>Email Compliance Desk</h5>
                      <p>Send an email to <a href="mailto:support@soloway.lk">support@soloway.lk</a> with your <strong>Booking Reference ID</strong> and reason for cancellation.</p>
                    </div>
                    <div className="step-card">
                      <span className="step-number">3</span>
                      <h5>Compliance Ticket</h5>
                      <p>Use the <strong>Contact Compliance</strong> button on this page to submit an official refund tracking or dispute inquiry 24/7.</p>
                    </div>
                  </div>

                  <div className="official-contact-card">
                    <h4>SoloWay Refund & Governance Department</h4>
                    <p><FaEnvelope /> Email: <a href="mailto:support@soloway.lk">support@soloway.lk</a> / <a href="mailto:refunds@soloway.lk">refunds@soloway.lk</a></p>
                    <p><FaClock /> Support Desk Hours: 24 Hours / 7 Days a Week</p>
                    <p><FaBuilding /> SOLOWAY.LK (PVT) LTD (Company No. PV 00373363), Colombo, Sri Lanka</p>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* TAB: PAYMENTS (PAYMENTS.LK & KOKO) */}
            {/* ========================================================= */}
            {activeTab === "payments" && (
              <div className="policy-section-content animate-fade-in">
                {/* Policy Meta Banner */}
                <div className="policy-meta-bar">
                  <div className="meta-left">
                    <span className="meta-tag">Payment Architecture</span>
                    <span className="meta-date">In-Force: Version 2026.09.01 | Draft: Version 2.0.0 (Gateway: Payments.lk & KOKO)</span>
                  </div>
                  <button
                    className="copy-policy-link-btn"
                    onClick={() => handleCopyCurrentLink("/policies?tab=payments")}
                    title="Copy direct link to Payment Policies"
                  >
                    {copiedUrl ? <FaCheck /> : <FaCopy />} {copiedUrl ? "Link Copied!" : "Copy Payment Terms"}
                  </button>
                </div>

                <div className="section-title-wrap">
                  <div className="section-icon-badge green"><FaCreditCard /></div>
                  <div>
                    <h2>Payment Providers & Payment Methods</h2>
                    <p className="section-subtitle">
                      Bank-grade payment processing through Payments.lk hosted gateway and Koko installment terms.
                    </p>
                  </div>
                </div>

                <div className="payment-policy-box payments-lk">
                  <div className="payment-policy-header">
                    <span className="payment-logo-placeholder"><FaCreditCard /></span>
                    <div>
                      <h3>Payments.lk Hosted Gateway</h3>
                      <p className="payment-badge">PCI-DSS Compliant Infrastructure • Powered by Payments.lk (Brand operated by Payable (Pvt) Ltd)</p>
                    </div>
                  </div>
                  <div className="payment-policy-body">
                    <p>
                      Payments.lk provides bank-grade, 256-bit SSL encrypted card and digital payment channels for all SoloWay reservations. 
                      Accepted methods include <strong>Visa, MasterCard, American Express, LankaPay, LankaQR, and supported local digital payment channels</strong>.
                    </p>
                    <div className="payment-options-row">
                      <div className="payment-option-card">
                        <div className="option-tag full">Full Payment</div>
                        <h4>100% Full Payment Checkout</h4>
                        <p>Complete booking amount is authorized at checkout. Reservation is confirmed upon verified server-to-server gateway webhook callback.</p>
                      </div>
                      <div className="payment-option-card">
                        <div className="option-tag advance">Advance Deposit</div>
                        <h4>50% Advance Booking Deposit</h4>
                        <p>Available on select accommodations. Pay 50% deposit at checkout to secure booking; balance settled prior to arrival according to host listing terms.</p>
                      </div>
                    </div>

                    <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #0284c7', fontSize: '13px', color: '#475569' }}>
                      <strong>Historical Gateway Transition:</strong> SoloWay has transitioned its active card checkout flows to Payments.lk. Transactions completed under prior gateway arrangements remain safely recorded in historical transaction ledgers for accounting, customer support, and financial audit continuity.
                    </div>
                  </div>
                </div>

                <div className="payment-policy-box koko">
                  <div className="payment-policy-header">
                    <span className="payment-logo-placeholder koko-icon"><FaMoneyBillWave /></span>
                    <div>
                      <h3>KOKO Buy Now Pay Later (BNPL)</h3>
                      <p className="payment-badge-koko">Optional 3-Month Installments</p>
                    </div>
                  </div>
                  <div className="payment-policy-body">
                    <p>KOKO allows eligible Sri Lankan travelers to split booking payments into 3 interest-free monthly installments based on KOKO provider terms.</p>
                    <ul>
                      <li><strong>Eligibility:</strong> Validated automatically at checkout based on listing settings, booking category, and minimum transaction thresholds.</li>
                      <li><strong>Installment Schedule:</strong> First installment billed immediately at checkout; remaining installments billed directly by KOKO.</li>
                      <li><strong>Visa Exclusion:</strong> Government tourist visa processing application fees are strictly full-payment-only and do not support KOKO installments.</li>
                    </ul>
                  </div>
                </div>

                <div className="security-assurance-banner">
                  <FaLock className="security-icon" />
                  <div>
                    <h4>Zero Card Data Retention Guarantee</h4>
                    <p>
                      SoloWay never captures, stores, or handles credit/debit card numbers, CVVs, or card passwords. All transaction tokenization is executed directly within Payments.lk / Payable's certified secure hosted infrastructure.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* TAB: WITHDRAWALS & PAYOUTS */}
            {/* ========================================================= */}
            {activeTab === "withdrawals" && (
              <div className="policy-section-content animate-fade-in">
                <div className="section-title-wrap">
                  <div className="section-icon-badge blue"><FaHandshake /></div>
                  <div>
                    <h2>Host & Guide Payout Policy</h2>
                    <p className="section-subtitle">
                      Structured balance transitions, T+3 safety hold periods, and verified banking disbursements for Hosts, Guides, and Agents.
                    </p>
                  </div>
                </div>

                <div className="payout-metrics-grid">
                  <div className="metric-box">
                    <div className="metric-icon"><FaUniversity /></div>
                    <div className="metric-number">LKR 2,000</div>
                    <div className="metric-title">Minimum Withdrawal</div>
                    <p className="metric-sub">Minimum balance required to generate an electronic withdrawal request.</p>
                  </div>

                  <div className="metric-box">
                    <div className="metric-icon"><FaClock /></div>
                    <div className="metric-number">T+3 Days</div>
                    <div className="metric-title">Dispute Safety Period</div>
                    <p className="metric-sub">Provider balance transitions from PENDING to AVAILABLE 3 days post-checkout.</p>
                  </div>

                  <div className="metric-box">
                    <div className="metric-icon"><FaFileInvoiceDollar /></div>
                    <div className="metric-number">24–48 Hrs</div>
                    <div className="metric-title">Disbursement Speed</div>
                    <p className="metric-sub">Electronic bank disbursements processed directly to verified Sri Lankan bank accounts.</p>
                  </div>
                </div>

                <h3 className="policy-subsection-title"><FaClock /> Provider Balance Lifecycle</h3>
                <div className="clearance-timeline">
                  <div className="timeline-step">
                    <div className="step-badge">1. PENDING</div>
                    <h4>Service Completed</h4>
                    <p>Guest checks out or tour concludes. Net eligible share held in Pending Balance.</p>
                  </div>
                  <div className="timeline-arrow">➔</div>
                  <div className="timeline-step">
                    <div className="step-badge">2. T+3 HOLD</div>
                    <h4>Safety & Dispute Window</h4>
                    <p>Protects against unaddressed traveler complaints and ensures verified service delivery.</p>
                  </div>
                  <div className="timeline-arrow">➔</div>
                  <div className="timeline-step">
                    <div className="step-badge">3. AVAILABLE</div>
                    <h4>Withdrawal Ready</h4>
                    <p>Funds become Available. Host or Guide submits payout request (min. LKR 2,000).</p>
                  </div>
                  <div className="timeline-arrow">➔</div>
                  <div className="timeline-step">
                    <div className="step-badge">4. WITHDRAWN</div>
                    <h4>Bank Transfer</h4>
                    <p>Disbursed via Electronic Bank Transfer (CEFT / SLIPS / Bank Payouts) to verified Sri Lankan bank accounts.</p>
                  </div>
                </div>

                <div className="policy-dual-list">
                  <div className="dual-card list-left">
                    <div className="card-header-icon"><FaLock color="#3b82f6" /></div>
                    <h3>KYC & Identity Verification Policy</h3>
                    <p>To ensure regulatory compliance and traveler safety, hosts must complete KYC before receiving payouts:</p>
                    <ul>
                      <li><strong>National Identity:</strong> Valid Sri Lankan NIC or Passport.</li>
                      <li><strong>Guides:</strong> Tour Guide credentials or SLTDA license where applicable.</li>
                      <li><strong>Hotels:</strong> Business registration / trade certificate and proof of property authority.</li>
                      <li><strong>Bank Account:</strong> Verified bank details matching registered legal entity or host name.</li>
                    </ul>
                  </div>

                  <div className="dual-card list-right">
                    <div className="card-header-icon"><FaAward color="#10b981" /></div>
                    <h3>Listing Governance & Quality Standards</h3>
                    <p>Soloway maintains platform quality through listing capacity guidelines:</p>
                    <div className="limit-gauge">
                      <div className="gauge-label">Standard Account Limit</div>
                      <div className="gauge-value">3 Active Listings</div>
                    </div>
                    <ul>
                      <li>Standard Guides and Hotel Owners can publish up to <strong>3 active listings</strong>.</li>
                      <li>Verified SuperHosts with high ratings (4.5+) and proven track records receive extended listing capacity.</li>
                      <li>Authorized Traveler Agents can manage partner hotel portfolios.</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* TAB: COMMISSIONS & SPLITS */}
            {/* ========================================================= */}
            {activeTab === "commissions" && (
              <div className="policy-section-content animate-fade-in">
                <div className="section-title-wrap">
                  <div className="section-icon-badge purple"><FaPercent /></div>
                  <div>
                    <h2>Platform Commission & Revenue Split</h2>
                    <p className="section-subtitle">
                      Auditable financial breakdown: 10% platform pool with up to 90% eligible provider share and dedicated Agent commissions.
                    </p>
                  </div>
                </div>

                <div className="commission-highlight-card">
                  <div className="commission-rate">10%</div>
                  <div className="commission-details">
                    <h3>Platform Pool Commission</h3>
                    <p>
                      Soloway allocates a transparent 10% platform fee on completed hotel stays and guided tours. 
                      This pool funds platform operations, AI concierge services, agent referral rewards, payment security, and traveler guarantees.
                    </p>
                  </div>
                </div>

                <h3 className="policy-subsection-title"><FaFileInvoiceDollar /> Eligible Revenue Breakdown</h3>
                <div className="revenue-split-visual">
                  <div className="split-bar host-share" style={{ width: "90%" }}>
                    <span>Up to 90% Eligible Provider Share</span>
                  </div>
                  <div className="split-bar platform-share" style={{ width: "10%" }}>
                    <span>10% Platform Pool</span>
                  </div>
                </div>

                <div className="split-legend-cards">
                  <div className="split-card host">
                    <div className="split-percent">Up to 90%</div>
                    <h4>Eligible Provider Share</h4>
                    <p>Allocated to hotel owner or guide, subject to applicable cancellation tier deductions, dispute holds, or penalties.</p>
                  </div>

                  <div className="split-card agent">
                    <div className="split-percent">3% – 5%</div>
                    <h4>Traveler Agent Share</h4>
                    <p>When a booking is made under an Agent's managed portfolio, their commission is allocated from within the 10% platform pool.</p>
                  </div>

                  <div className="split-card soloway">
                    <div className="split-percent">5% – 7%</div>
                    <h4>Soloway Operations</h4>
                    <p>Funds 24/7 emergency response, SafeStay guarantee reserves, infrastructure, customer support, and marketing.</p>
                  </div>
                </div>

                <div className="info-box-premium">
                  <FaInfoCircle className="info-icon" />
                  <div>
                    <h4>Auditable Financial Accounting</h4>
                    <p>
                      All gross amounts, platform commissions, agent shares, refund deductions, and net payouts are calculated dynamically 
                      and permanently recorded in the immutable financial audit log.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* TAB: DISPUTES & SAFETY */}
            {/* ========================================================= */}
            {activeTab === "disputes" && (
              <div className="policy-section-content animate-fade-in">
                <div className="section-title-wrap">
                  <div className="section-icon-badge orange"><FaShieldAlt /></div>
                  <div>
                    <h2>Trust, Safety & Dispute Policy</h2>
                    <p className="section-subtitle">
                      Formal dispute resolution, evidence review, SafeStay guarantee, and host payout freezing mechanisms.
                    </p>
                  </div>
                </div>

                <div className="dispute-cards-grid">
                  <div className="dispute-box">
                    <div className="dispute-header">
                      <FaClock className="dispute-icon" />
                      <h4>24-Hour Arrival Dispute Window</h4>
                    </div>
                    <p>
                      If a property or tour departs substantially from its listing description, the traveler may open a dispute 
                      within <strong>24 hours of arrival</strong>. Opening a dispute automatically freezes provider payout pending review.
                    </p>
                  </div>

                  <div className="dispute-box">
                    <div className="dispute-header">
                      <FaAward className="dispute-icon" />
                      <h4>Soloway SafeStay Guarantee</h4>
                    </div>
                    <p>
                      If a host fails to provide the confirmed service, Soloway concierge facilitates rebooking into an equivalent/higher available property 
                      or issues a full refund under administrative governance.
                    </p>
                  </div>

                  <div className="dispute-box">
                    <div className="dispute-header">
                      <FaBan className="dispute-icon red" />
                      <h4>Evidence & Admin Review</h4>
                    </div>
                    <p>
                      Disputes follow a multi-step review (DISPUTE_OPENED → HOST_RESPONSE → ADMIN_REVIEW). 
                      Refunds are approved based on verified photographic evidence, host statements, and policy criteria.
                    </p>
                  </div>

                  <div className="dispute-box">
                    <div className="dispute-header">
                      <FaLock className="dispute-icon" />
                      <h4>Immutable Financial Audit Trails</h4>
                    </div>
                    <p>
                      Every financial event, dispute status update, and balance adjustment is permanently recorded in the financial audit log.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* TAB: ROLES & KYC */}
            {/* ========================================================= */}
            {activeTab === "roles" && (
              <div className="policy-section-content animate-fade-in">
                <div className="section-title-wrap">
                  <div className="section-icon-badge blue"><FaUserFriends /></div>
                  <div>
                    <h2>Ecosystem Roles & Governance</h2>
                    <p className="section-subtitle">
                      Defined responsibilities and KYC standards across Soloway platform participants.
                    </p>
                  </div>
                </div>
                
                <div className="role-cards-grid">
                  <div className="role-premium-card traveler">
                    <h3>Traveler</h3>
                    <p className="role-tagline">The Explorer</p>
                    <ul>
                      <li>Book verified hotel stays, excursions, and cultural experiences.</li>
                      <li>Earn loyalty coins and access SafeStay protection.</li>
                      <li>Eligible for 24-hour arrival dispute window.</li>
                    </ul>
                  </div>

                  <div className="role-premium-card guide">
                    <h3>Tour Guide</h3>
                    <p className="role-tagline">The Local Expert</p>
                    <ul>
                      <li>Publish authentic tour itineraries and guide services.</li>
                      <li>Receive eligible earnings following T+3 safety hold.</li>
                      <li>Verified through tourism/license and identity verification.</li>
                    </ul>
                  </div>

                  <div className="role-premium-card hotelowner">
                    <h3>Hotel Owner</h3>
                    <p className="role-tagline">The Accommodation Host</p>
                    <ul>
                      <li>List accommodations, manage room rates, and receive guest reservations.</li>
                      <li>Withdraw eligible net room revenue (up to 90%) to company bank accounts.</li>
                      <li>Verified through property ownership/lease evidence and business credentials.</li>
                    </ul>
                  </div>

                  <div className="role-premium-card agent">
                    <h3>Traveler Agent</h3>
                    <p className="role-tagline">The Network Partner</p>
                    <ul>
                      <li>Onboard and mentor hotels and guides onto the Soloway network.</li>
                      <li>Earn 3%–5% revenue share from the 10% platform pool on portfolio bookings.</li>
                      <li>Access agent analytics and automated commission accounting.</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* TAB: LOYALTY COINS */}
            {/* ========================================================= */}
            {activeTab === "coins" && (
              <div className="policy-section-content animate-fade-in">
                <div className="section-title-wrap">
                  <div className="section-icon-badge yellow"><FaCoins /></div>
                  <div>
                    <h2>Soloway Loyalty Coins Policy</h2>
                    <p className="section-subtitle">
                      Accounting lifecycle and redemption rules for Soloway platform rewards.
                    </p>
                  </div>
                </div>

                <div className="coins-policy-grid">
                  <div className="coin-rule-card">
                    <div className="coin-val-circle">1 Coin = LKR 1</div>
                    <h4>Redemption Value</h4>
                    <p>1 Soloway Coin equals LKR 1 platform redemption value towards eligible bookings.</p>
                  </div>

                  <div className="coin-rule-card">
                    <div className="coin-val-circle">1% – 5%</div>
                    <h4>Cashback on Completed Stays</h4>
                    <p>Earn cashback in Loyalty Coins upon successful completion of qualifying stays and tours.</p>
                  </div>

                  <div className="coin-rule-card">
                    <div className="coin-val-circle">Max 20%</div>
                    <h4>Checkout Redemption Cap</h4>
                    <p>Redeem accumulated available coins to cover up to 20% of an eligible checkout total.</p>
                  </div>
                </div>

                <div className="info-box-premium">
                  <FaInfoCircle className="info-icon" />
                  <div>
                    <h4>Coin Accounting Lifecycle & Reversals</h4>
                    <p>
                      Coins earned from a booking remain in <code>COINS_PENDING</code> until the booking is completed. 
                      If a booking is refunded or cancelled, any coins earned from that reservation are reversed, and redeemed coins are restored where applicable. 
                      Coins remain valid for 12 months from issuance.
                    </p>
                  </div>
                </div>
              </div>
            )}

          </main>
        </div>
      </div>

      {/* Compliance & Support Interactive Modal */}
      {showSupportModal && (
        <div className="compliance-modal-overlay" onClick={resetModalState}>
          <div
            className="compliance-modal-container"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="compliance-modal-header">
              <div className="modal-title-box">
                <div className="modal-title-icon">
                  <FaHeadset />
                </div>
                <div>
                  <h3>Soloway Compliance & Support Desk</h3>
                  <p className="modal-subtitle">
                    24/7 dedicated support for policy clarifications, refund audits, and dispute escalations
                  </p>
                </div>
              </div>
              <button
                className="compliance-modal-close"
                onClick={resetModalState}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            {/* Quick Access Action Cards */}
            <div className="compliance-quick-grid">
              <div className="compliance-quick-card" onClick={handleOpenAiConcierge}>
                <div className="quick-icon-wrap ai-purple">
                  <FaRobot />
                </div>
                <div className="quick-card-text">
                  <h4>24/7 AI Policy Assistant</h4>
                  <p>Get instant answers on cancellation tiers, refund rules, and payout formulas.</p>
                  <span className="quick-card-action">Launch AI Concierge →</span>
                </div>
              </div>

              <div
                className="compliance-quick-card"
                onClick={() => {
                  setInquiryType("dispute_arrival");
                  setFormData((prev) => ({
                    ...prev,
                    priority: "high",
                    subject: prev.subject || "24-Hour Stay/Tour Arrival Dispute Claim"
                  }));
                  const formEl = document.getElementById("compliance-ticket-form");
                  if (formEl) formEl.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <div className="quick-icon-wrap dispute-amber">
                  <FaShieldAlt />
                </div>
                <div className="quick-card-text">
                  <h4>SafeStay 24h Dispute Hold</h4>
                  <p>Open an official arrival claim to automatically freeze host payout pending audit.</p>
                  <span className="quick-card-action">File Dispute Claim ↓</span>
                </div>
              </div>
            </div>

            {/* SafeStay Dispute Notice */}
            <div className="compliance-safestay-alert">
              <FaShieldAlt className="safestay-icon" />
              <div>
                <strong>SafeStay 24-Hour Dispute Protection:</strong> If you are filing an arrival dispute (property mismatch, severe cleanliness, or guide absence), filing an official ticket below immediately freezes the provider's payout pending compliance audit.
              </div>
            </div>

            {/* Ticket Submission Form / Confirmation */}
            {submitSuccess ? (
              <div className="compliance-success-view">
                <div className="success-icon-badge">
                  <FaCheckCircle />
                </div>
                <h3>Compliance Ticket Registered</h3>
                <p className="ticket-id-tag">
                  Ticket Reference: <strong>#{submitSuccess.id || "CONFIRMED"}</strong>
                </p>
                <p className="success-desc">
                  Your inquiry has been logged in the Soloway compliance and audit queue. Our compliance desk reviews inquiries within 12–24 hours and will email status updates to <strong>{formData.email}</strong>.
                </p>
                <div className="success-btn-group">
                  <button
                    type="button"
                    className="compliance-btn secondary"
                    onClick={handleOpenAiConcierge}
                  >
                    <FaRobot /> Chat with AI While Waiting
                  </button>
                  <button
                    type="button"
                    className="compliance-btn primary"
                    onClick={resetModalState}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form id="compliance-ticket-form" className="compliance-form" onSubmit={handleSubmitTicket}>
                <h4 className="form-section-heading">
                  <FaPaperPlane className="section-head-icon" /> Submit Formal Compliance Inquiry or Dispute
                </h4>

                {submitError && (
                  <div className="compliance-error-alert">
                    <FaExclamationTriangle /> {submitError}
                  </div>
                )}

                <div className="compliance-form-grid">
                  <div className="form-field">
                    <label>Inquiry Category *</label>
                    <select
                      value={inquiryType}
                      onChange={(e) => setInquiryType(e.target.value)}
                      className="compliance-select"
                    >
                      <option value="cancellation">Cancellation & Refund Policy Question</option>
                      <option value="dispute_arrival">24-Hour Stay/Tour Arrival Dispute</option>
                      <option value="payout_hold">Host Payout / Withdrawal Delay Inquiry</option>
                      <option value="kyc_verification">Identity & KYC Verification Support</option>
                      <option value="payment_gateway">Payment Gateway / Charge Dispute</option>
                      <option value="general_compliance">Other Governance / Compliance Inquiry</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Priority Level</label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleFormChange}
                      className="compliance-select"
                    >
                      <option value="low">Low (General Query)</option>
                      <option value="medium">Medium (Standard Inquiry)</option>
                      <option value="high">High (Urgent Stay / Arrival Dispute)</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Your Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g. John Doe"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="compliance-input"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="compliance-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Booking ID or Reference Number (Optional)</label>
                  <input
                    type="text"
                    name="bookingRef"
                    placeholder="e.g. BKG-84920 or Tour #42"
                    value={formData.bookingRef}
                    onChange={handleFormChange}
                    className="compliance-input"
                  />
                </div>

                <div className="form-field">
                  <label>Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    placeholder="Brief summary of your inquiry..."
                    value={formData.subject}
                    onChange={handleFormChange}
                    className="compliance-input"
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Detailed Message & Context *</label>
                  <textarea
                    name="message"
                    rows="4"
                    placeholder="Please describe the issue, relevant dates, and any supporting details..."
                    value={formData.message}
                    onChange={handleFormChange}
                    className="compliance-textarea"
                    required
                  />
                </div>

                <div className="compliance-modal-footer">
                  <button
                    type="button"
                    className="compliance-btn secondary"
                    onClick={resetModalState}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="compliance-btn primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting Ticket..." : "Submit to Compliance Desk"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Global Footer */}
      <Footer />
    </div>
  );
}

export default Policies;
